import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import Calendar from 'iconsax-react/dist/esm/Calendar';
import { TDR } from 'tdr-common';
import { AvailabilityGuardContext, SearchFilters, serializeSearchFiltersForURL } from '../../routes/AvailabilityGuard';
import { AnnouncementResponse } from '../announcements/common';
import { CardShell } from './CardShell';
import { TableCategoryCounts } from './categorizeTables';
import { emitTableClicked } from './cardAnalytics';
import { analytics } from '../../helpers/analytics';
import { RESULT_CLICKED } from '../../helpers/analytics/constants';

type CrossSellDateCardProps = {
    restaurant: TDR.Restaurant;
    table: TDR.Table;
    searchFilters: SearchFilters;
    announcement?: AnnouncementResponse;
    viewedTable?: boolean;
    counts: TableCategoryCounts;
    totalResults: number;
    position: number;
    hasScrolledToBottom?: boolean;
    timeslotData?: TDR.TimeSlotMap;
    soldOut?: boolean;
};

const Footer = ({ onClick }: { onClick: () => void }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-tablz-med-gray-1 flex items-center justify-between p-3 rounded-2xl text-sm">
            <div className="flex gap-1 items-center">
                <Calendar size={16} color="#FACC15" variant="Bold" />
                <p className="font-poppins text-yellow-400">
                    <Trans
                        i18nKey="inventory.available_on_other_dates"
                        components={[<span key="b" className="font-semibold" />]}
                    />
                </p>
            </div>
            <button
                type="button"
                className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
                onClick={onClick}
                data-testid="see-details-btn"
            >
                {t('inventory.see_details')}
            </button>
        </div>
    );
};

export const CrossSellDateCard = ({
    restaurant, table, searchFilters, announcement, viewedTable,
    counts, totalResults, position, hasScrolledToBottom, timeslotData,
    soldOut = true
}: CrossSellDateCardProps) => {
    const navigate = useNavigate();
    const { setPreviousTime, setPreviousDate } = useOutletContext<AvailabilityGuardContext>();

    const handleClick = () => {
        setPreviousTime(searchFilters.time || '');
        setPreviousDate(searchFilters.date || '');
        analytics.track(RESULT_CLICKED, { resultType: 'table date cross sell' });
        emitTableClicked({
            table, restaurantId: restaurant.id, searchFilters, counts, totalResults,
            position, tableStatus: soldOut ? 'date_sold_out' : 'date_closed', announcement, hasScrolledToBottom
        });
        navigate({
            pathname: table.slug,
            search: serializeSearchFiltersForURL({ ...searchFilters, date: undefined, time: undefined }).toString()
        });
    };

    return (
        <CardShell
            restaurant={restaurant}
            table={table}
            searchFilters={searchFilters}
            announcement={announcement}
            viewedTable={viewedTable}
            soldOutOverlay={soldOut}
            onCardClick={handleClick}
            timeslotData={timeslotData}
            footer={<Footer onClick={handleClick} />}
        />
    );
};
